import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import type { SoundfontSource, SoundfontEntry } from "@midi-fantom-utils/player-core";
import { DEFAULT_SOUNDFONT_URL, soundfontCatalogUrl } from "../config.js";

type Kind = SoundfontSource["kind"];

type SoundFontSettingsProps = {
  source: SoundfontSource;
  onSourceChange: (next: SoundfontSource) => void;
  catalog: ReadonlyArray<SoundfontEntry>;
};

export const SoundFontSettings = ({
  source,
  onSourceChange,
  catalog,
}: SoundFontSettingsProps) => {
  const [kind, setKind] = useState<Kind>(source.kind);
  const [urlDraft, setUrlDraft] = useState(
    source.kind === "url" ? source.url : ""
  );
  const [catalogId, setCatalogId] = useState(
    source.kind === "catalog" ? source.id : ""
  );
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (source.kind === "url") setUrlDraft(source.url);
    if (source.kind === "catalog") setCatalogId(source.id);
    setKind(source.kind);
  }, [source]);

  // blob URL lifecycle:
  // - созданный здесь URL передаётся наверх через onSourceChange и оттуда
  //   попадает в MidiPlayer → createSoundfontEngine. Settings сохраняют
  //   его в localStorage, но это «мёртвая» ссылка при следующей загрузке
  //   страницы — blob: аннулируется при reload. Чтобы не оставлять
  //   висящих ссылок:
  //   * при выборе нового файла — отзываем старый (см. onFile);
  //   * при смене `source.kind` на не-"file" (catalog/url) — отзываем
  //     blob, потому что плеер уже не должен его использовать;
  //   * при размонтировании компонента (закрытие модалки, переключение
  //     таба) — НЕ отзываем: пока родитель жив, он может переиспользовать
  //     URL (MidiPlayer получает тот же `settings.soundfont`). Очистка
  //     в этом случае — ответственность App (см. cleanup blob в onClose).
  //   Сама blob-жизнь в части persistence управляется через saveSettings/
  //   loadSettings: см. packages/web/app/src/config.ts — `kind: "file"`
  //   НЕ сохраняется в localStorage и при следующей загрузке
  //   восстанавливается как дефолтный URL.
  useEffect(() => {
    if (source.kind !== "file" && objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, [source.kind]);

  const applyCatalog = (id: string) => {
    const entry = catalog.find((c) => c.id === id);
    if (!entry) return;
    onSourceChange({
      kind: "catalog",
      id: entry.id,
      url: soundfontCatalogUrl(entry.file),
      label: entry.label,
    });
  };

  const applyUrl = (url: string) => {
    const trimmed = url.trim();
    if (trimmed.length === 0) return;
    onSourceChange({ kind: "url", url: trimmed });
  };

  const onKindChange = (next: Kind) => {
    setKind(next);
    if (next === "catalog" && catalog.length > 0) {
      const id = catalogId || catalog[0]!.id;
      applyCatalog(id);
    } else if (next === "url") {
      const trimmed = urlDraft.trim();
      if (trimmed.length > 0) {
        applyUrl(trimmed);
      } else {
        setUrlDraft(DEFAULT_SOUNDFONT_URL);
        onSourceChange({ kind: "url", url: DEFAULT_SOUNDFONT_URL });
      }
    }
  };

  const onUrlDraftChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUrlDraft(e.target.value);
  };

  const onUrlApply = () => applyUrl(urlDraft);

  const onCatalogSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    setCatalogId(e.target.value);
    applyCatalog(e.target.value);
  };

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    const blobUrl = URL.createObjectURL(file);
    objectUrlRef.current = blobUrl;
    onSourceChange({ kind: "file", url: blobUrl, name: file.name });
    e.target.value = "";
  };

  return (
    <div className="settings-section">
      <p className="muted">Источник звука для браузерного плеера.</p>
      <label className="settings-row">
        <input
          type="radio"
          name="settings-soundfont-kind"
          value="catalog"
          checked={kind === "catalog"}
          onChange={() => onKindChange("catalog")}
        />
        <span className="settings-row__label">Из каталога</span>
        <select
          value={catalogId}
          onChange={onCatalogSelect}
          disabled={catalog.length === 0}
          aria-label="Soundfont из каталога"
        >
          {catalog.length === 0 ? (
            <option value="">Каталог пуст</option>
          ) : (
            catalog.map((entry) => {
              const suffix = entry.license
                ? `, ${entry.license}`
                : "";
              return (
                <option key={entry.id} value={entry.id}>
                  {entry.label} ({Math.round(entry.bytes / 1024 / 1024)} МБ
                  {suffix})
                </option>
              );
            })
          )}
        </select>
      </label>

      <label className="settings-row">
        <input
          type="radio"
          name="settings-soundfont-kind"
          value="url"
          checked={kind === "url"}
          onChange={() => onKindChange("url")}
        />
        <span className="settings-row__label">Ссылка (CDN)</span>
        <input
          type="text"
          value={urlDraft}
          onChange={onUrlDraftChange}
          onBlur={onUrlApply}
          onKeyDown={(e) => {
            if (e.key === "Enter") onUrlApply();
          }}
          placeholder="https://…/sound.sf2"
          spellCheck={false}
          aria-label="URL SoundFont"
          className="settings-row__input"
        />
      </label>

      <label className="settings-row">
        <input
          type="radio"
          name="settings-soundfont-kind"
          value="file"
          checked={kind === "file"}
          onChange={() => onKindChange("file")}
        />
        <span className="settings-row__label">Загрузить файл</span>
        <input
          type="file"
          accept=".sf2"
          onChange={onFile}
          aria-label="Загрузить .sf2 файл"
        />
      </label>
    </div>
  );
};